/**
 * Data layer for `campaign-wise-invoice-grouping`. Thin typed fetch wrappers over /api/v1/campaign-wise-invoice-grouping.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CampaignWiseInvoiceGroupingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCampaignWiseInvoiceGroupingResponse {
	count: number;
	items: CampaignWiseInvoiceGroupingRow[];
}

export interface CreateCampaignWiseInvoiceGroupingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/campaign-wise-invoice-grouping";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCampaignWiseInvoiceGroupingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCampaignWiseInvoiceGroupingResponse>(res);
}

export async function create(body: CreateCampaignWiseInvoiceGroupingBody): Promise<CampaignWiseInvoiceGroupingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CampaignWiseInvoiceGroupingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const campaignWiseInvoiceGroupingQueryKeys = {
	all: ["campaign-wise-invoice-grouping"] as const,
	list: () => [...campaignWiseInvoiceGroupingQueryKeys.all] as const,
	detail: (id: string) => [...campaignWiseInvoiceGroupingQueryKeys.all, id] as const,
};
